/**
 * Dependency-free HMAC-SHA256 (RFC 2104) over ./sha256.ts. The backend core keys the at-rest hashes of bearer tokens
 * with the server's AUTH_SECRET through it; like SHA-256 it has to run in every backend mode, including the in-browser
 * InProcess backend (no node:crypto, and crypto.subtle is async-only).
 */
import { utf8Encode } from '../json';
import { sha256Bytes, toHex } from './sha256';

const BLOCK = 64;

/** A reusable HMAC-SHA256 key: the padded inner/outer key blocks are computed once. */
export class HmacSha256 {
  private readonly inner: Uint8Array;
  private readonly outer: Uint8Array;

  constructor(key: Uint8Array | string) {
    let k = typeof key === 'string' ? utf8Encode(key) : key;
    if (k.length > BLOCK) k = sha256Bytes(k);
    this.inner = new Uint8Array(BLOCK);
    this.outer = new Uint8Array(BLOCK);
    for (let i = 0; i < BLOCK; i++) {
      const b = i < k.length ? k[i] : 0;
      this.inner[i] = b ^ 0x36;
      this.outer[i] = b ^ 0x5c;
    }
  }

  digest(message: Uint8Array | string): Uint8Array {
    const m = typeof message === 'string' ? utf8Encode(message) : message;
    const a = new Uint8Array(BLOCK + m.length);
    a.set(this.inner);
    a.set(m, BLOCK);
    const innerHash = sha256Bytes(a);
    const b = new Uint8Array(BLOCK + innerHash.length);
    b.set(this.outer);
    b.set(innerHash, BLOCK);
    return sha256Bytes(b);
  }

  hex(message: Uint8Array | string): string {
    return toHex(this.digest(message));
  }
}

/** HMAC-SHA256 of `message` under `key`, lower-case hex. */
export function hmacSha256Hex(key: Uint8Array | string, message: Uint8Array | string): string {
  return new HmacSha256(key).hex(message);
}
